import { useMemo, useState } from "react";
import { CheckCheck, Eye, Pencil, Trash2 } from "lucide-react";
import moment from "moment";

import { AddTaskModal } from "components/AddTaskModal/AddTaskModal";
import { getPatientName } from "components/Header/utils/getPatientName";
import { DisplayTaskModal } from "components/Modal/DisplayTaskModal/DisplayTaskModal";
import { DataTable } from "components/ui/DataTable";
import { Pill } from "components/ui/Pill";
import { PILL_COLORS } from "components/ui/Pill/pillColors";
import { useClosedPaginatedTasks } from "features/comunications/hooks/useClosedPaginatedTasks";
import { useCompleteTask } from "features/comunications/hooks/useCompleteTask";
import { useDeleteTask } from "features/comunications/hooks/useDeleteTask";
import { getTaskAssignmentLabel } from "features/comunications/utils/formatTaskAssignment";

const PRIORITY_LABELS = { 0: "High", 1: "Medium", 2: "Low" };
const PRIORITY_PILL = {
  0: PILL_COLORS.reactionYes,
  1: PILL_COLORS.statusWarning,
  2: PILL_COLORS.reactionNo,
};
const STATUS_PILL = {
  Open: PILL_COLORS.reactionUnknown,
  Held: PILL_COLORS.statusWarning,
  Cancelled: PILL_COLORS.reactionYes,
  Complete: PILL_COLORS.reactionNo,
};

export const ClosedTaskTable = ({ goToPatientDetails }) => {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [editTask, setEditTask] = useState(null);

  const { data, isLoading } = useClosedPaginatedTasks({
    search: "",
    page,
    rows,
  });
  const { mutate: deleteTask } = useDeleteTask();
  const { mutate: completeTask } = useCompleteTask();

  const columns = useMemo(
    () => [
      {
        id: "patient",
        header: "Patient",
        width: "170px",
        render: (item) => getPatientName(item.patient) || "N/A",
        getTooltipText: (item) => getPatientName(item.patient) || "N/A",
        onClick: (item) =>
          item.patient?.id &&
          goToPatientDetails(item.patient.id, getPatientName(item.patient)),
        clickableWhen: (item) => !!item.patient?.id,
      },
      {
        id: "title",
        header: "Instruction",
        width: "300px",
        render: (item) => item.title || "-",
        getTooltipText: (item) => item.title || "-",
        onClick: (item) => setSelectedTaskId(item.id),
        clickableSubtle: true,
      },
      {
        id: "assignedTo",
        header: "Assigned To",
        width: "170px",
        render: (item) => getTaskAssignmentLabel(item),
        getTooltipText: (item) => getTaskAssignmentLabel(item),
      },
      {
        id: "priority",
        header: "Priority",
        width: "90px",
        render: (item) => {
          const label = PRIORITY_LABELS[item.priority];
          const colors = PRIORITY_PILL[item.priority];
          return label ? <Pill {...colors}>{label}</Pill> : "-";
        },
      },
      {
        id: "dueDate",
        header: "Due Date",
        width: "110px",
        render: (item) =>
          item.dueDate ? moment(item.dueDate).format("DD-MM-YYYY") : "N/A",
      },
      {
        id: "status",
        header: "Status",
        width: "minmax(100px, 1fr)",
        render: (item) => {
          const colors =
            STATUS_PILL[item.status] || PILL_COLORS.reactionUnknown;
          return item.status ? <Pill {...colors}>{item.status}</Pill> : "-";
        },
      },
      {
        id: "closedDatetime",
        header: "Date Closed",
        width: "minmax(110px, 1fr)",
        render: (item) =>
          item.closedDatetime
            ? moment(item.closedDatetime).format("DD-MM-YYYY")
            : "N/A",
      },
    ],
    [goToPatientDetails]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Complete",
        icon: CheckCheck,
        primary: true,
        visibleWhen: (item) => item.status !== "Complete",
        onClick: (item) => completeTask(item.id),
      },
      {
        label: "View",
        icon: Eye,
        primary: true,
        onClick: (item) => setSelectedTaskId(item.id),
      },
      {
        label: "Edit",
        icon: Pencil,
        primary: true,
        onClick: (item) => setEditTask(item),
      },
      {
        label: "Delete",
        icon: Trash2,
        primary: true,
        onClick: (item) => deleteTask(item.id),
      },
    ],
    [deleteTask, completeTask]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={data?.data || []}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        emptyTitle="No closed tasks"
        emptyText="You don't have any completed tasks at this time."
        currentPage={page}
        onPageChange={setPage}
        totalCount={data?.totalRecords}
        rowsValue={rows}
        onRowsChange={(val) => {
          setRows(val);
          setPage(1);
        }}
        rowActions={rowActions}
      />
      {selectedTaskId && (
        <DisplayTaskModal
          taskId={selectedTaskId}
          isOpen={!!selectedTaskId}
          closeModal={() => setSelectedTaskId(null)}
        />
      )}
      <AddTaskModal
        task={editTask}
        modalTitle="Edit Task"
        buttonText="Edit Task"
        noButton
        isOpen={!!editTask}
        setIsOpen={(open) => {
          if (!open) setEditTask(null);
        }}
      />
    </>
  );
};
